import React, { useEffect, useState } from 'react';

import { IoClose } from "react-icons/io5";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";

import { apiBaseUrl } from '../../core/globalVariables'

const FilterFeatureDataSimpleModal = ({ globalRefs, globalState }) => {

    const mapMetaData = globalState.mapMetaData;

    const setShowFilterFeatureDataSimpleModal =
        globalState.setShowFilterFeatureDataSimpleModal;

    const setMapMetaData = globalState.setMapMetaData;

    const currentMapIdRef = globalRefs.currentMapIdRef;

    const featureDataSimple =
        mapMetaData?.define_feature_data_simple || {};

    const [featureData, setFeatureData] = useState([]);
    const [draggedFeature, setDraggedFeature] = useState(null);
    const [saving, setSaving] = useState(false);

    /*
     * Build the initial list from map metadata.
     *
     * display_order controls ordering.
     *
     * display_order === -1
     *     = hidden
     *
     * Anything else, including null / undefined,
     *     = visible.
     *
     * Visible properties are always placed before hidden properties.
     */
    useEffect(() => {
        const features =
            globalRefs.longstandingGeoJSONRef?.current?.features || [];

        const items = Object.entries(featureDataSimple)
            .filter(([key]) => key !== 'shortname')
            .map(([key, meta]) => ({
                key,
                meta
            }))
            .filter(item => {
                if (!item.meta || typeof item.meta !== 'object') {
                    return false;
                }

                return features.some(feature => {
                    const featureData =
                        feature?.feature_data_simple;

                    return (
                        featureData &&
                        Object.prototype.hasOwnProperty.call(
                            featureData,
                            item.key
                        )
                    );
                });
            })
            .sort((a, b) => {
                const aVisible =
                    a.meta.display_order !== -1;

                const bVisible =
                    b.meta.display_order !== -1;

                if (aVisible !== bVisible) {
                    return aVisible ? -1 : 1;
                }

                const orderA =
                    typeof a.meta.display_order === 'number' &&
                        a.meta.display_order !== -1
                        ? a.meta.display_order
                        : Number.MAX_SAFE_INTEGER;

                const orderB =
                    typeof b.meta.display_order === 'number' &&
                        b.meta.display_order !== -1
                        ? b.meta.display_order
                        : Number.MAX_SAFE_INTEGER;

                if (orderA !== orderB) {
                    return orderA - orderB;
                }

                return (
                    a.meta?.name || a.key
                ).localeCompare(
                    b.meta?.name || b.key,
                    undefined,
                    {
                        sensitivity: 'base'
                    }
                );
            });

        // Initial load ONLY establishes the available feature data.
        // Do not set selectedKey or selectedValue here.
        setFeatureData(items);

    }, [
        featureDataSimple,
        globalRefs
    ]);

    /*
     * Toggle visibility locally.
     *
     * This does not change display_order directly.
     *
     * Turning ON:
     *     Move to bottom of visible section.
     *
     * Turning OFF:
     *     Move to bottom of hidden section.
     */
    const toggleFeatureData = (key) => {

        setFeatureData(prev => {

            const index = prev.findIndex(
                item => item.key === key
            );

            if (index === -1) {
                return prev;
            }

            const item = prev[index];

            const currentlyVisible =
                item.meta.display_order !== -1;

            const updatedItem = {
                ...item,
                meta: {
                    ...item.meta,

                    /*
                     * We use the current state only to determine
                     * visibility. Actual display_order values are
                     * regenerated when Confirm is pressed.
                     */
                    display_order: currentlyVisible
                        ? -1
                        : null
                }
            };

            const remaining = prev.filter(
                feature => feature.key !== key
            );

            /*
             * Turning ON.
             */
            if (!currentlyVisible) {

                const visible = remaining.filter(
                    feature =>
                        feature.meta.display_order !== -1
                );

                const hidden = remaining.filter(
                    feature =>
                        feature.meta.display_order === -1
                );

                return [
                    ...visible,
                    updatedItem,
                    ...hidden
                ];
            }

            /*
             * Turning OFF.
             */
            const visible = remaining.filter(
                feature =>
                    feature.meta.display_order !== -1
            );

            const hidden = remaining.filter(
                feature =>
                    feature.meta.display_order === -1
            );

            return [
                ...visible,
                ...hidden,
                updatedItem
            ];

        });

    };

    /*
     * Drag handling.
     */
    const handleDragStart = (event, key) => {

        setDraggedFeature({
            key
        });

        event.dataTransfer.effectAllowed = 'move';

        event.dataTransfer.setData(
            'text/plain',
            JSON.stringify({ key })
        );

    };

    const handleDragOver = event => {

        event.preventDefault();

        event.dataTransfer.dropEffect = 'move';

    };

    const handleDrop = (event, targetKey) => {

        event.preventDefault();

        if (!draggedFeature) {
            return;
        }

        setFeatureData(prev => {

            const fromIndex = prev.findIndex(
                item => item.key === draggedFeature.key
            );

            const toIndex = prev.findIndex(
                item => item.key === targetKey
            );

            if (
                fromIndex === -1 ||
                toIndex === -1 ||
                fromIndex === toIndex
            ) {
                return prev;
            }

            const next = [...prev];

            const [movedItem] =
                next.splice(fromIndex, 1);

            next.splice(
                toIndex,
                0,
                movedItem
            );

            return next;

        });

        setDraggedFeature(null);

    };

    const handleDragEnd = () => {

        setDraggedFeature(null);

    };

    /*
     * Confirm all changes.
     */

    const confirmChanges = async () => {

        try {

            setSaving(true);

            const token =
                localStorage.getItem('token');

            if (!token) {
                throw new Error(
                    'No token in local storage'
                );
            }

            const mapId =
                currentMapIdRef.current;

            if (!mapId) {
                throw new Error(
                    'No current map ID'
                );
            }

            const simpleFeatureData = {};

            let visibleIndex = 0;

            featureData.forEach(item => {

                const isVisible =
                    item.meta.display_order !== -1;

                simpleFeatureData[item.key] = {

                    display_order: isVisible
                        ? visibleIndex++
                        : -1

                };

            });

            const payload = {

                feature_header: {

                    define_feature_data_simple:
                        simpleFeatureData

                }

            };

            console.log(
                'PATCH PAYLOAD:',
                payload
            );

            const url =
                `${apiBaseUrl}/api/maps/${mapId}?srid=4326`;

            const response = await fetch(url, {

                method: 'PATCH',

                headers: {

                    Authorization:
                        `Bearer ${token}`,

                    'Content-Type':
                        'application/json',

                },

                body: JSON.stringify(payload),

            });

            if (!response.ok) {

                const errorText =
                    await response.text();

                throw new Error(
                    `PATCH failed: ${response.status} ${errorText}`
                );

            }

            await response.json();

            /*
             * Update global metadata to match
             * the confirmed local state.
             */
            setMapMetaData(prev => {

                const next =
                    structuredClone(prev);

                if (
                    !next.define_feature_data_simple
                ) {
                    next.define_feature_data_simple = {};
                }

                Object.entries(
                    simpleFeatureData
                ).forEach(
                    ([key, values]) => {

                        next
                            .define_feature_data_simple[key] = {

                            ...next
                                .define_feature_data_simple[key],

                            ...values

                        };

                    }
                );

                return next;

            });

            setShowFilterFeatureDataSimpleModal(
                false
            );

        } catch (err) {

            console.error(
                'PATCH failed:',
                err
            );

        } finally {

            setSaving(false);

        }

    };

    /*
     * Render a feature row.
     */
    const renderFeatureData = (item) => {

        const {
            key,
            meta
        } = item;

        const isVisible =
            meta.display_order !== -1;

        const isDragging =
            draggedFeature?.key === key;

        return (

            <div

                key={key}
                className={`propertyFilterOption ${isVisible ? 'is-selected' : 'is-deselected'}`}

                draggable

                onDragStart={event =>
                    handleDragStart(
                        event,
                        key
                    )
                }

                onDragOver={handleDragOver}

                onDrop={event =>
                    handleDrop(
                        event,
                        key
                    )
                }

                onDragEnd={handleDragEnd}

                style={{

                    display: 'flex',

                    alignItems: 'center',

                    gap: '8px',

                    padding: '6px',

                    marginBottom: '6px',

                    border:
                        '1px solid #ddd',

                    borderRadius: '6px',

                    background:
                        isVisible
                            ? '#fff'
                            : '#f3f3f3',

                    opacity:
                        isDragging
                            ? 0.5
                            : 1,

                    cursor: 'grab',

                    userSelect: 'none'

                }}

            >

                <span

                    style={{

                        cursor: 'grab',

                        color: '#999',

                        fontSize: '16px',

                        lineHeight: 1

                    }}

                    title="Drag to reorder"

                >

                    ⋮⋮

                </span>

                <span

                    style={{

                        flex: 1,

                        color:
                            isVisible
                                ? '#222'
                                : '#999'

                    }}

                >

                    {meta?.name || key}

                </span>

                <button

                    onClick={event => {

                        event.stopPropagation();

                        toggleFeatureData(key);

                    }}

                    style={{

                        border: 'none',

                        background: 'none',

                        padding: '2px',

                        display: 'flex',

                        alignItems: 'center',

                        cursor: 'pointer',

                        fontSize: '20px',

                        color:
                            isVisible
                                ? '#222'
                                : '#999'

                    }}

                    title={
                        isVisible
                            ? 'Hide feature data'
                            : 'Show feature data'
                    }

                >

                    {isVisible
                        ? <IoMdEye />
                        : <IoMdEyeOff />
                    }

                </button>

            </div>

        );

    };

    return (

        <div

            style={{

                width: '320px',

                height: '500px',

                backgroundColor: '#fff',

                position: 'absolute',

                left: '50%',

                top: '50%',

                transform:
                    'translate(-50%, -50%)',

                zIndex: 10000000,

                borderRadius: '6px',

                padding: '12px',

                boxShadow:
                    '0 10px 30px rgba(0,0,0,0.2)',

            }}

        >

            <div

                style={{

                    width: '320px',

                    maxHeight: '450px',

                    overflowY: 'auto',

                    backgroundColor: '#fff',

                }}

            >

                <div

                    style={{

                        display: 'flex',

                        justifyContent:
                            'space-between',

                        alignItems:
                            'center'

                    }}

                >

                    <h4 style={{ margin: 0 }}>

                        Properties Filter

                    </h4>

                    <button

                        onClick={() =>
                            setShowFilterFeatureDataSimpleModal(
                                false
                            )
                        }

                        style={{

                            border: 'none',

                            background: 'none',

                            cursor: 'pointer',
                            paddingRight: '10px'

                        }}

                    >

                        <IoClose />

                    </button>

                </div>

                <h4
                    style={{
                        marginTop: 10
                    }}
                >
                    Info
                </h4>

                {featureData.map(item =>
                    renderFeatureData(item)
                )}

            </div>

            <div

                style={{

                    zIndex: '100',

                    height: '50px',

                    width: '100%'

                }}

            >

                <button

                    onClick={confirmChanges}

                    disabled={saving}

                    style={{

                        width: '100%',

                        marginTop: '16px',

                        padding: '8px 12px',

                        border: 'none',

                        borderRadius: '6px',

                        background:
                            saving
                                ? '#aaa'
                                : '#073028',

                        color: '#fff',

                        cursor:
                            saving
                                ? 'default'
                                : 'pointer',

                        fontSize: '14px'

                    }}

                >

                    {saving
                        ? 'Saving...'
                        : 'Confirm'}

                </button>

            </div>

        </div>

    );

};

export default FilterFeatureDataSimpleModal;
